/**
 * signet-login integration: the "sign in with Nostr" path.
 *
 * Wraps signet-login so the rest of the pad only deals with the Account
 * shape from identity.ts. Signing in is the player's own convenience on
 * this phone. Nothing from the session (name, key, picture, profile) is
 * kept or sent anywhere: the signer is closed straight away and only the
 * fact of the sign-in is remembered. The game name is always the three
 * arcade initials the player picks.
 *
 * The signet-app origin is read from localStorage so a user running
 * their own signet-app can point at it (a freedom-tech requirement:
 * never hard-default silently to the hosted instance).
 */

import { login, handleRedirectCallback, logout } from 'signet-login';
import type { SignetSession } from 'signet-login';
import { adoptSignetAccount, type Account } from './identity.js';

const SIGNET_ORIGIN_KEY = 'joystick:signet-origin';
const APP_NAME = 'Joystick';

/** The user's chosen signet-app origin, or '' if they have not set
 *  one (signet-login then uses its own default). */
export function getSignetAppOrigin(): string {
  try {
    return (localStorage.getItem(SIGNET_ORIGIN_KEY) ?? '').trim();
  } catch {
    return '';
  }
}

/** Persist (or clear) the user's signet-app origin. */
export function setSignetAppOrigin(origin: string): void {
  try {
    const clean = origin.trim();
    if (clean) localStorage.setItem(SIGNET_ORIGIN_KEY, clean);
    else localStorage.removeItem(SIGNET_ORIGIN_KEY);
  } catch {
    /* ignore */
  }
}

/** Turn a SignetSession into a persisted Account. The live signer is
 *  closed and the session's name and key are discarded. */
async function adoptSession(session: SignetSession): Promise<Account> {
  try { await session.signer.close(); } catch { /* ignore */ }
  return adoptSignetAccount({ method: session.method });
}

/** Show the signet-login picker and resolve to an Account on success,
 *  or null on cancel / timeout. */
export async function signIn(): Promise<Account | null> {
  const origin = getSignetAppOrigin();
  try {
    const session = await login({
      appName: APP_NAME,
      theme: 'dark',
      ...(origin ? { signetAppOrigin: origin } : {}),
    });
    if (!session) return null;
    return await adoptSession(session);
  } catch (err) {
    console.warn('[pad] signet sign-in failed', err);
    return null;
  }
}

/** Consume a same-tab redirect / Amber callback on boot. Returns an
 *  Account if the user is arriving back from signet-app, else null. */
export async function consumeSignetRedirect(): Promise<Account | null> {
  try {
    const result = await handleRedirectCallback();
    if (result.kind === 'session') {
      return await adoptSession(result.session);
    }
  } catch (err) {
    console.warn('[pad] signet redirect callback failed', err);
  }
  return null;
}

/** Clear signet-login's stored session — the "switch account" path
 *  for a signed-in user. */
export async function signetLogout(): Promise<void> {
  try {
    await logout();
  } catch {
    /* ignore — the pad-side record is cleared separately */
  }
}
